import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { requireFeatureGate, requireSystemSubsidiaryAdmin } from '~/utils/guards';

/** URL search: the open batch (job uuid), so a review panel can be deep-linked. */
interface BatchReviewSearch {
  batch?: string;
}

const BatchReviewPage = lazy(() => import('~/features/batch-review/pages/batch-review-page'));

const requirePlatformAdmin = requireSystemSubsidiaryAdmin();
const requireBatchReviewFlag = requireFeatureGate('batch-review');

// Lily AI platform admins only — super admin AND the active subsidiary is the system one, the
// rule the workflow-service API enforces (403 otherwise) — then the `batch-review` feature flag
// (see docs/batch-review.md).
export const Route = createFileRoute('/_authenticated/_tenant/platform/batch-review')({
  beforeLoad: async () => {
    requirePlatformAdmin();
    await requireBatchReviewFlag();
  },
  validateSearch: (search: Record<string, unknown>): BatchReviewSearch =>
    typeof search.batch === 'string' && search.batch ? { batch: search.batch } : {},
  component: RouteComponent,
});

/** Binds the `?batch=` search param to the page's open-batch state. */
function RouteComponent() {
  const { batch } = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <Suspense fallback={<PageSkeleton />}>
      <BatchReviewPage
        activeJobId={batch}
        onActiveJobChange={(jobId) => {
          void navigate({
            search: (prev) => {
              const rest = { ...prev };
              delete rest.batch;
              return jobId ? { ...rest, batch: jobId } : rest;
            },
            replace: true,
          });
        }}
      />
    </Suspense>
  );
}
